// Logică de domeniu pură (relații de familie, istoric doze, generarea programului
// zilnic de medicamente) — fără dependențe de React.
import { todayISO, isoD, shouldMedBeActiveOnDate } from './helpers';

// ─── RELAȚII DE FAMILIE ──────────────────────────────────────────────────────
export function getSecondDegreeRelation(myRelationToMid, midRelationToNew) {
  // myRelationToMid = relația mea față de persoana intermediară (ex: "Fiică" față de Tată)
  // midRelationToNew = relația persoanei intermediare față de cea nouă (ex: Tată adaugă "Mamă")
  const map = {
    "Fiică->Mamă": "Bunică",   "Fiică->Tată": "Bunic",
    "Fiu->Mamă": "Bunică",     "Fiu->Tată": "Bunic",
    "Fiică->Frate": "Unchi",   "Fiică->Soră": "Mătușă",
    "Fiu->Frate": "Unchi",     "Fiu->Soră": "Mătușă",
    "Soție->Mamă": "Soacră",   "Soție->Tată": "Socru",
    "Soț->Mamă": "Soacră",     "Soț->Tată": "Socru",
    "Fiică->Fiu": "Frate",     "Fiică->Fiică": "Soră",
    "Fiu->Fiu": "Frate",       "Fiu->Fiică": "Soră",
  };
  return map[`${myRelationToMid}->${midRelationToNew}`] || null;
}

export function getInverseRelation(relation, targetSex) {
  // targetSex: 'M' = masculin, 'F' = feminin
  const isFem = targetSex === 'F';
  const map = {
    "Tată": isFem ? "Fiică" : "Fiu",
    "Mamă": isFem ? "Fiică" : "Fiu",
    "Fiu": isFem ? "Mamă" : "Tată",
    "Fiică": isFem ? "Mamă" : "Tată",
    "Soț": "Soție",
    "Soție": "Soț",
    "Frate": isFem ? "Soră" : "Frate",
    "Soră": isFem ? "Soră" : "Frate",
    "Bunic": isFem ? "Nepoată" : "Nepot",
    "Bunică": isFem ? "Nepoată" : "Nepot",
    "Unchi": isFem ? "Nepoată" : "Nepot",
    "Mătușă": isFem ? "Nepoată" : "Nepot",
    "Nepot": isFem ? "Mătușă" : "Unchi",
    "Nepoată": isFem ? "Mătușă" : "Unchi",
    "Văr": isFem ? "Vară" : "Văr",
    "Vară": isFem ? "Vară" : "Văr",
  };
  return map[relation] || "Rudă";
}

// ─── ISTORIC DOZE ────────────────────────────────────────────────────────────
// O doză merită păstrată ca istoric dacă: e confirmată/sărită SAU a fost adăugată manual (schedIdx>=9000 sau notă manual)
export function isHistoricDose(dose){
  if(!dose) return false;
  if(dose.status==="done" || dose.status==="skipped") return true;
  if(typeof dose.schedIdx==="number" && dose.schedIdx>=9000) return true; // doză adăugată manual
  if(dose.note && dose.note.includes("manual")) return true;
  return false;
}
// Verifică dacă un medicament are intrări de păstrat FĂRĂ nume salvat (istoric în pericol la ștergere)
export function medHasUnnamedHistory(medId, medLogs){
  let total=0, named=0;
  Object.values(medLogs||{}).forEach(dayLog=>{
    (dayLog[medId]||[]).forEach(dose=>{
      if(isHistoricDose(dose)){ total++; if(dose.medName) named++; }
    });
  });
  return total>0 && named<total; // există intrări, dar nu toate au nume
}

// ─── PROGRAM ZILNIC MEDICAMENTE ──────────────────────────────────────────────
export function buildDayLog(meds, dateISO) {
  const date = dateISO || todayISO();
  const log = {};
  meds.forEach(m => {
    if(!m.active) return;
    if(!shouldMedBeActiveOnDate(m, date)) return;

    // interval_hours — generează doze și adaugă cele din ziua precedentă care depășesc miezul nopții
    if(m.scheduleType === "interval_hours") {
      const hours = parseInt(m.intervalHours,10) || 8;
      if(hours < 24) {
        const firstTime = m.schedule[0]?.time || "09:00";
        const dose = m.schedule[0]?.dose || "1";
        const [fh, fm] = firstTime.split(":").map(Number);
        const doses = [];
        let idx = 0;

        // Doze din ziua curentă
        let current = fh * 60 + fm;
        while(current < 24 * 60) {
          const hh = String(Math.floor(current/60)).padStart(2,"0");
          const mm = String(current%60).padStart(2,"0");
          doses.push({schedIdx:idx, time:`${hh}:${mm}`, dose, status:"pending", note:"", actualTime:""});
          current += hours * 60;
          idx++;
        }

        // Doze din ziua precedentă care depășesc miezul nopții și ajung azi
        // Ex: start 09:00, interval 8h → 09, 17, 01 (noaptea = ziua următoare)
        const prevDate = new Date(date + "T12:00:00");
        prevDate.setDate(prevDate.getDate() - 1);
        const prevDateISO = isoD(prevDate);
        if(shouldMedBeActiveOnDate(m, prevDateISO)) {
          let prevCurrent = fh * 60 + fm;
          while(prevCurrent < 48 * 60) { // 48h ca să prindem toate
            if(prevCurrent >= 24 * 60) {
              const minuteOfDay = prevCurrent - 24 * 60;
              const hh = String(Math.floor(minuteOfDay/60)).padStart(2,"0");
              const mm = String(minuteOfDay%60).padStart(2,"0");
              doses.unshift({schedIdx:idx, time:`${hh}:${mm}`, dose, status:"pending", note:"", actualTime:""});
              idx++;
            }
            prevCurrent += hours * 60;
          }
        }

        // Sortează după oră
        doses.sort((a,b) => a.time.localeCompare(b.time));
        // Re-indexează
        doses.forEach((d,i) => d.schedIdx = i);

        log[m.id] = doses;
        return;
      }
    }

    log[m.id] = (m.schedule||[]).map((s,i) => ({
      schedIdx:i, time:s.time||"", dose:s.dose||"1",
      status:"pending", note:"", actualTime:""
    }));
  });
  return log;
}
